import "./CustomerDetailsModal.scss";

import { Badge, Button, Dropdown, Form, Modal, Nav } from "react-bootstrap";
import {
  faBirthdayCake,
  faCalendarCheck,
  faChartLine,
  faChevronDown,
  faCircleInfo,
  faClockRotateLeft,
  faCopy,
  faFilter,
  faMagnifyingGlass,
  faMoneyBill,
  faPhone,
  faUser,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { formatDate, formatInReal, formatPhone } from "../../../commons/util/HallAppUtil";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import AppContainer from "../../../commons/containers/AppContainer";
import AppLoading from "../../../commons/AppLoading";
import CustomerAPI from "../CustomerAPI";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";

const customerAPI = new CustomerAPI();

/** tenta ler datas em dd/MM/yyyy ou ISO */
function safeParseDate(input) {
  if (!input) return null;

  if (typeof input === "string" && input.includes("/")) {
    const [dd, mm, yyyy] = input.split("/").map((x) => parseInt(x, 10));
    if (!Number.isNaN(dd) && !Number.isNaN(mm) && !Number.isNaN(yyyy)) {
      return new Date(yyyy, mm - 1, dd);
    }
  }

  const d = new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

function monthKeyFromDate(dateObj) {
  if (!dateObj) return "Sem data";
  const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
  const yyyy = dateObj.getFullYear();
  return `${mm}/${yyyy}`;
}

function normalizeText(s) {
  return String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");
}

function CustomerDetailsModal({ closeShow, show, customer }) {
  const [isLoading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  // UI state
  const [activeTab, setActiveTab] = useState("visits");
  const [q, setQ] = useState("");
  const [period, setPeriod] = useState("ALL"); // ALL | 30D | 90D | 12M
  const [groupMode, setGroupMode] = useState("MONTH"); // MONTH | NONE
  const [sortMode, setSortMode] = useState("DATE_DESC"); // DATE_DESC | DATE_ASC | VALUE_DESC | VALUE_ASC

  const searchInputRef = useRef(null);

  const getHistory = useCallback(async () => {
    if (!customer?.id) return;
    try {
      setLoading(true);
      const response = await customerAPI.getHistory(customer.id);
      setHistory(Array.isArray(response.data) ? response.data : []);
    } catch {
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [customer?.id]);

  useEffect(() => {
    if (show) {
      setActiveTab("visits");
      setQ("");
      setPeriod("ALL");
      setGroupMode("MONTH");
      setSortMode("DATE_DESC");

      getHistory();

      setTimeout(() => {
        try {
          searchInputRef.current?.focus();
        } catch { }
      }, 250);
    }
  }, [show, getHistory]);

  const whatsappLink = useMemo(() => {
    const raw = customer?.whatsapp ? String(customer.whatsapp) : "";
    const digits = raw.replace(/\D/g, "");
    const withCountry = digits.startsWith("55") ? digits : digits ? `55${digits}` : "";
    return withCountry ? `https://wa.me/${withCountry}` : null;
  }, [customer?.whatsapp]);

  const telLink = useMemo(() => {
    const raw = customer?.whatsapp ? String(customer.whatsapp) : "";
    const digits = raw.replace(/\D/g, "");
    return digits ? `tel:${digits}` : null;
  }, [customer?.whatsapp]);

  const copyPhone = async () => {
    try {
      const text = customer?.whatsapp ? formatPhone(customer.whatsapp) : "";
      if (!text) return;
      await navigator.clipboard.writeText(text);
      // HallAppToast.success("Número copiado!");
    } catch {
      // HallAppToast.error("Não foi possível copiar.");
    }
  };

  // filtros + ordenação
  const filteredHistory = useMemo(() => {
    const now = new Date();
    const cutoff = (() => {
      if (period === "30D") return new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
      if (period === "90D") return new Date(now.getFullYear(), now.getMonth(), now.getDate() - 90);
      if (period === "12M") return new Date(now.getFullYear(), now.getMonth() - 12, now.getDate());
      return null;
    })();

    const query = normalizeText(q);

    const base = history
      .map((it) => ({ ...it, _dateObj: safeParseDate(it.serviceDate) }))
      .filter((it) => {
        if (cutoff && it._dateObj) {
          if (it._dateObj.getTime() < cutoff.getTime()) return false;
        }

        if (!query) return true;

        const hay = normalizeText(
          `${it.serviceName || ""} ${it.employeeName || ""} ${it.notes || ""}`
        );
        return hay.includes(query);
      });

    return base.sort((a, b) => {
      const av = Number(a.value) || 0;
      const bv = Number(b.value) || 0;
      const ad = a._dateObj?.getTime() || 0;
      const bd = b._dateObj?.getTime() || 0;

      switch (sortMode) {
        case "DATE_ASC":
          return ad - bd;
        case "VALUE_DESC":
          return bv - av;
        case "VALUE_ASC":
          return av - bv;
        case "DATE_DESC":
        default:
          return bd - ad;
      }
    });
  }, [history, q, period, sortMode]);

  const groupedHistory = useMemo(() => {
    if (groupMode === "NONE") return [];

    const map = new Map();
    for (const it of filteredHistory) {
      const key = monthKeyFromDate(it._dateObj);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    }

    const entries = Array.from(map.entries()).map(([key, items]) => {
      items.sort((a, b) => (b._dateObj?.getTime() || 0) - (a._dateObj?.getTime() || 0));
      return [key, items];
    });

    entries.sort((a, b) => {
      const ad = a[1]?.[0]?._dateObj?.getTime() || 0;
      const bd = b[1]?.[0]?._dateObj?.getTime() || 0;
      return bd - ad;
    });

    return entries;
  }, [filteredHistory, groupMode]);

  const stats = useMemo(() => {
    const totalSpent = filteredHistory.reduce((acc, it) => acc + (Number(it.value) || 0), 0);
    const totalVisits = filteredHistory.length;

    const visitsByMonth = new Map();
    const spentByMonth = new Map();

    for (const it of filteredHistory) {
      const key = monthKeyFromDate(it._dateObj);
      visitsByMonth.set(key, (visitsByMonth.get(key) || 0) + 1);
      spentByMonth.set(key, (spentByMonth.get(key) || 0) + (Number(it.value) || 0));
    }

    const months = visitsByMonth.size || 0;
    const avgPerMonth = months === 0 ? 0 : Math.round(totalVisits / months);
    const avgTicket = totalVisits === 0 ? 0 : Math.round((totalSpent / totalVisits) * 100) / 100;

    let bestMonth = null;
    let bestMonthValue = -1;
    for (const [k, v] of spentByMonth.entries()) {
      if (v > bestMonthValue) {
        bestMonthValue = v;
        bestMonth = k;
      }
    }

    const last = filteredHistory
      .filter((x) => x._dateObj)
      .sort((a, b) => (b._dateObj?.getTime() || 0) - (a._dateObj?.getTime() || 0))[0];

    return {
      totalSpent,
      totalVisits,
      avgPerMonth,
      avgTicket,
      bestMonth,
      bestMonthValue: bestMonthValue > 0 ? bestMonthValue : 0,
      lastVisitDate: last?._dateObj ? formatDate(last.serviceDate) : null,
    };
  }, [filteredHistory]);

  const StatPill = ({ icon, label, value }) => (
    <div className="hc-cd-pill">
      <div className="hc-cd-pill__label">
        <FontAwesomeIcon icon={icon} className="me-2" />
        {label}
      </div>
      <div className="hc-cd-pill__value">{value}</div>
    </div>
  );

  const VisitItem = ({ visit }) => {
    const value = Number(visit.value) || 0;

    return (
      <div className="hc-cd-visit" role="button" tabIndex={0}>
        <div className="hc-cd-visit__left">
          <div className="hc-cd-visit__title">
            <span className="hc-cd-dot" aria-hidden="true" />
            <span className="text-truncate">{visit.serviceName || "Serviço"}</span>
          </div>

          <div className="hc-cd-visit__meta">
            <span className="me-3">
              <FontAwesomeIcon icon={faCalendarCheck} className="me-2" />
              {visit.serviceDate ? formatDate(visit.serviceDate) : "Data não informada"}
            </span>

            {visit.employeeName ? (
              <span className="text-truncate">
                <FontAwesomeIcon icon={faUser} className="me-2" />
                {visit.employeeName}
              </span>
            ) : null}
          </div>

          {visit.notes ? (
            <div className="hc-cd-visit__notes">
              <FontAwesomeIcon icon={faCircleInfo} className="me-2" />
              <span className="text-truncate">{visit.notes}</span>
            </div>
          ) : null}
        </div>

        <div className="hc-cd-visit__right">
          <Badge className="hc-cd-badge" pill>
            {formatInReal(value)}
          </Badge>
        </div>
      </div>
    );
  };

  const EmptyState = ({ title, subtitle }) => (
    <div className="hc-cd-empty">
      <div className="hc-cd-empty__icon">
        <FontAwesomeIcon icon={faClockRotateLeft} />
      </div>
      <div className="hc-cd-empty__title">{title}</div>
      <div className="hc-cd-empty__subtitle">{subtitle}</div>
    </div>
  );

  return (
    <Modal
      show={show}
      onHide={closeShow}
      centered
      scrollable
      size="lg"
      fullscreen={true}
      contentClassName="hc-cd-modal"
    >
      <Modal.Header className="hc-cd-header">
        <div className="hc-cd-header__row">
          <div className="hc-cd-header__titles">
            <div className="hc-cd-header__title">Detalhes do cliente</div>
            <div className="hc-cd-header__subtitle">Informações, histórico e insights.</div>
          </div>

          <button
            type="button"
            className="btn btn-link p-0 hc-cd-header__close"
            onClick={closeShow}
            aria-label="Fechar"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      </Modal.Header>

      <Modal.Body className="hc-cd-body">
        <div className="container">
          <div className="hc-cd-card">
            <div className="hc-cd-card__top">
              <div className="d-flex align-items-center gap-2 min-w-0">
                <div className="hc-cd-avatar" aria-hidden="true">
                  <FontAwesomeIcon icon={faUser} />
                </div>

                <div className="min-w-0">
                  <div className="hc-cd-name text-truncate">{customer?.name || "Cliente"}</div>

                  <div className="hc-cd-meta">
                    {customer?.birthday ? (
                      <span className="me-3">
                        <FontAwesomeIcon icon={faBirthdayCake} className="me-2" />
                        {customer.birthday}
                      </span>
                    ) : (
                      <span className="me-3">Aniversário não informado</span>
                    )}

                    {customer?.lastVisit ? (
                      <span>
                        <FontAwesomeIcon icon={faCalendarCheck} className="me-2" />
                        Última visita: {formatDate(customer.lastVisit)}
                      </span>
                    ) : (
                      <span>
                        <FontAwesomeIcon icon={faCalendarCheck} className="me-2" />
                        Última visita: —
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="hc-cd-actions">
                {whatsappLink && (
                  <a
                    className="btn btn-sm hc-cd-btnPrimary"
                    href={whatsappLink}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <FontAwesomeIcon icon={faWhatsapp} className="me-2" />
                    WhatsApp
                  </a>
                )}

                {telLink && (
                  <a className="btn btn-outline-dark btn-sm" href={telLink}>
                    <FontAwesomeIcon icon={faPhone} className="me-2" />
                    Ligar
                  </a>
                )}

                {customer?.whatsapp && (
                  <Button variant="outline-dark" size="sm" onClick={copyPhone}>
                    <FontAwesomeIcon icon={faCopy} className="me-2" />
                    Copiar
                  </Button>
                )}
              </div>
            </div>

            {customer?.whatsapp ? (
              <div className="hc-cd-phone">
                <FontAwesomeIcon icon={faPhone} className="me-2" />
                {formatPhone(customer.whatsapp)}
              </div>
            ) : null}

            <div className="hc-cd-pills">
              <StatPill icon={faMoneyBill} label="Total gasto" value={formatInReal(stats.totalSpent)} />
              <StatPill icon={faChartLine} label="Visitas/mês" value={stats.avgPerMonth} />
              <StatPill icon={faMoneyBill} label="Ticket médio" value={formatInReal(stats.avgTicket)} />
            </div>
          </div>

          <div className="hc-cd-tabs">
            <Nav variant="pills" activeKey={activeTab} onSelect={(k) => setActiveTab(k || "visits")}>
              <Nav.Item>
                <Nav.Link eventKey="visits">Visitas</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="insights">Insights</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="notes">Anamnese</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="notes">Observações</Nav.Link>
              </Nav.Item>
            </Nav>
          </div>

          {activeTab === "visits" && (
            <>
              <div className="hc-cd-toolbar">
                <div className="hc-cd-search">
                  <FontAwesomeIcon icon={faMagnifyingGlass} className="hc-cd-search__icon" />
                  <Form.Control
                    ref={searchInputRef}
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Buscar serviço, profissional, observação..."
                    aria-label="Buscar no histórico"
                  />
                </div>

                <div className="hc-cd-toolbarRight">
                  <Dropdown align="end">
                    <Dropdown.Toggle className="hc-cd-filterBtn" size="sm" variant="outline-dark">
                      <FontAwesomeIcon icon={faFilter} className="me-2" />
                      Filtros
                      <FontAwesomeIcon icon={faChevronDown} className="ms-2" />
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="hc-cd-filterMenu">
                      <div className="px-3 py-2">
                        <div className="hc-cd-filterTitle">Período</div>
                        <div className="d-flex flex-wrap gap-2">
                          {[
                            { k: "ALL", t: "Tudo" },
                            { k: "30D", t: "30 dias" },
                            { k: "90D", t: "90 dias" },
                            { k: "12M", t: "12 meses" },
                          ].map((opt) => (
                            <Button
                              key={opt.k}
                              size="sm"
                              variant={period === opt.k ? "dark" : "outline-dark"}
                              onClick={() => setPeriod(opt.k)}
                            >
                              {opt.t}
                            </Button>
                          ))}
                        </div>

                        <hr className="my-3" />

                        <div className="hc-cd-filterTitle">Agrupar</div>
                        <div className="d-flex flex-wrap gap-2">
                          {[
                            { k: "MONTH", t: "Por mês" },
                            { k: "NONE", t: "Sem agrupar" },
                          ].map((opt) => (
                            <Button
                              key={opt.k}
                              size="sm"
                              variant={groupMode === opt.k ? "dark" : "outline-dark"}
                              onClick={() => setGroupMode(opt.k)}
                            >
                              {opt.t}
                            </Button>
                          ))}
                        </div>

                        <hr className="my-3" />

                        <div className="hc-cd-filterTitle">Ordenar</div>
                        <Form.Select size="sm" value={sortMode} onChange={(e) => setSortMode(e.target.value)}>
                          <option value="DATE_DESC">Mais recentes</option>
                          <option value="DATE_ASC">Mais antigas</option>
                          <option value="VALUE_DESC">Maior valor</option>
                          <option value="VALUE_ASC">Menor valor</option>
                        </Form.Select>
                      </div>
                    </Dropdown.Menu>
                  </Dropdown>

                  <div className="hc-cd-count">{filteredHistory.length} registro(s)</div>
                </div>
              </div>

              {isLoading ? (
                <div className="mt-3">
                  <AppLoading />
                </div>
              ) : filteredHistory.length === 0 ? (
                <EmptyState title="Nenhuma visita encontrada" subtitle="Tente ajustar a busca ou os filtros." />
              ) : groupMode === "MONTH" ? (
                <div className="hc-cd-list">
                  {groupedHistory.map(([month, items]) => (
                    <div key={month} className="hc-cd-group">
                      <div className="hc-cd-group__header">
                        <span className="hc-cd-group__badge">{month}</span>
                        <span className="hc-cd-group__meta">{items.length} visita(s)</span>
                      </div>

                      <div className="hc-cd-group__items">
                        {items.map((visit, idx) => (
                          <VisitItem key={`${month}-${idx}`} visit={visit} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="hc-cd-list">
                  <div className="hc-cd-group__items">
                    {filteredHistory.map((visit, idx) => (
                      <VisitItem key={`visit-${idx}`} visit={visit} />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {activeTab === "insights" && (
            <div className="hc-cd-panel">
              <div className="hc-cd-panel__title">Insights rápidos</div>

              <div className="hc-cd-grid">
                <div className="hc-cd-gridCard">
                  <div className="hc-cd-gridCard__label">Total gasto</div>
                  <div className="hc-cd-gridCard__value">{formatInReal(stats.totalSpent)}</div>
                </div>

                <div className="hc-cd-gridCard">
                  <div className="hc-cd-gridCard__label">Total de visitas (filtro atual)</div>
                  <div className="hc-cd-gridCard__value">{stats.totalVisits}</div>
                </div>

                <div className="hc-cd-gridCard">
                  <div className="hc-cd-gridCard__label">Ticket médio</div>
                  <div className="hc-cd-gridCard__value">{formatInReal(stats.avgTicket)}</div>
                </div>

                <div className="hc-cd-gridCard">
                  <div className="hc-cd-gridCard__label">Média de visitas / mês</div>
                  <div className="hc-cd-gridCard__value">{stats.avgPerMonth}</div>
                </div>

                <div className="hc-cd-gridCard">
                  <div className="hc-cd-gridCard__label">Última visita (histórico filtrado)</div>
                  <div className="hc-cd-gridCard__value">{stats.lastVisitDate || "—"}</div>
                </div>

                <div className="hc-cd-gridCard">
                  <div className="hc-cd-gridCard__label">Melhor mês (por gasto)</div>
                  <div className="hc-cd-gridCard__value">
                    {stats.bestMonth ? `${stats.bestMonth} • ${formatInReal(stats.bestMonthValue)}` : "—"}
                  </div>
                </div>
              </div>

              <div className="hc-cd-panel__hint">Os insights respeitam os filtros da aba “Visitas”.</div>
            </div>
          )}

          {activeTab === "notes" && (
            <div className="hc-cd-panel">
              <div className="hc-cd-panel__title">Observações</div>

              <div className="hc-cd-panel__text">
                Aqui você pode exibir/editar dados adicionais (ex.: alergias, preferências, restrições, tags e observações internas).
              </div>

              <EmptyState
                title="Em breve"
                subtitle="Me diga quais campos você quer que eu já monto o formulário com integração na API."
              />
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default CustomerDetailsModal;
